import NotifierService from '../Notifier/NotifierService'
import ApiService from '../Api/ApiService'
import Quill from 'quill'

let options = {
  uri: {
    generate: 'job_offers/[missionUri]/generate_content',
    status: 'job_offers/generate_content/[trackingId]/status',
  },
  payload: {},
  selector: 'data-generate-content',
  submitSelector: 'data-submit-after-generate',
  editorSelector: '[data-rich-text-editor]',
  titleSelector: '[data-job-offer-title]',
  pollInterval: 5000,
  pollTimeout: 120000,
  messages: {
    success: {
      generate: 'Der Inhalt wurde erfolgreich generiert!',
    },
    error: {
      generate: 'Der Inhalt konnte nicht generiert werden!',
      timeout: 'Die Generierung hat zu lange gedauert. Bitte versuche es erneut.',
    }
  }
}

/**
 * Singleton storage.
 */
let instance = null

/**
 * Singleton export.
 */
export default (userOptions) => {
  if (instance !== null) {
    return instance
  }
  return instance = new ContentGeneratorService(userOptions)
}

class ContentGeneratorService {
  constructor(userOptions) {
    options = { ...options, ...userOptions }
    this.state = null
    this.generated = false
    this._pollStartedAt = null
    return this
  }

  init() {
    this.elements = Array.from(document.querySelectorAll(`[${options.selector}]`))
    this.submitButton = document.querySelector(`[${options.submitSelector}]`)
    this.editor = document.querySelector(options.editorSelector)
    this.title = document.querySelector(options.titleSelector)

    if (!this.elements.length || !this.editor) {
      return this
    }

    this.initElements()
    this.initSubmitGuard()
    this.state = 'initialized'
    console.log('INIT ContentGenerator:', this)
    return this
  }

  initElements() {
    this.elements.forEach((element) => {
      element.dataset.label = element.textContent.trim()
      element.addEventListener('click', () => {
        if (this.isSubmitButton(element)) {
          this.submitForm(element)
          return
        }
        this.generateContent()
      })
    })
  }

  initSubmitGuard() {
    if (!this.submitButton) {
      return
    }
    this.submitButton.form.addEventListener('submit', (event) => {
      if (!this.generated) {
        event.preventDefault()
      }
    })
  }

  isSubmitButton(element) {
    return element === this.submitButton && this.generated
  }

  submitForm(element) {
    element.form.submit()
    element.disabled = true
  }

  generateContent() {
    this.state = 'waiting'
    this.disableButton()
    this.showEditorLoading()
    this.disableTitleInput()

    const endpoint = options.uri.generate

    return ApiService()
      .get(endpoint)
      .then((response) => {
        const trackingId = response.data.tracking_id
        console.log('Job enqueued, tracking_id:', trackingId)
        this._pollStartedAt = Date.now()
        this.pollStatus(trackingId)
      })
      .catch((error) => {
        console.log('Failed to enqueue generation job:', error)
        this.onError()
      })
  }

  pollStatus(trackingId) {
    if (Date.now() - this._pollStartedAt > options.pollTimeout) {
      this.onTimeout()
      return
    }

    const endpoint = options.uri.status.replace('[trackingId]', trackingId)

    ApiService()
      .get(endpoint)
      .then((response) => {
        const { status, content, errors } = response.data

        if (status === 'done') {
          this.onSuccess(content)
        } else if (status === 'failed') {
          this.onError(errors && errors[0])
        } else {
          setTimeout(() => this.pollStatus(trackingId), options.pollInterval)
        }
      })
      .catch((error) => {
        console.log('Polling failed:', error)
        this.onError()
      })
  }

  onSuccess(content) {
    this.state = 'success'

    if (content.description) {
      this.clearEditorLoading()
      const container = this.editor.previousSibling
      if (container && container.querySelector('.ql-editor')) {
        const quillEditor = Quill.find(container)
        if (quillEditor) {
          quillEditor.setText('')
          const cleanContent = content.description.replace(/\\n/g, '<br>')
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = cleanContent
          quillEditor.clipboard.dangerouslyPasteHTML(0, tempDiv.innerHTML)
          this.editor.value = quillEditor.root.innerHTML
        }
      }
    }

    if (content.title) {
      this.enableTitleInput()
      this.title.value = content.title
    }

    NotifierService().createNotification(options.messages.success.generate, 'success')
    this.finishGeneration()
  }

  onError(message) {
    this.state = 'error'
    this.clearEditorLoading()
    this.enableTitleInput()
    NotifierService().createNotification(message || options.messages.error.generate, 'error')
    this.finishGeneration()
  }

  onTimeout() {
    this.state = 'error'
    this.clearEditorLoading()
    this.enableTitleInput()
    NotifierService().createNotification(options.messages.error.timeout, 'error')
    this.finishGeneration()
  }
  
  finishGeneration() {
    if (this.submitButton && !this.generated) {
      this.generated = true
      this.submitButton.type = 'submit'
      this.submitButton.dataset.label = 'Speichern'
      this.elements.forEach((element) => element.classList.remove('d-none'))
    }
    this.enableButton()
  }

  disableButton() {
    this.elements.forEach((element) => {
      element.setAttribute('disabled', true)
      if (!this.isSubmitButton(element)) {
        element.textContent = 'Generiere...'
      }
    })
  }

  enableButton() {
    this.elements.forEach((element) => {
      element.removeAttribute('disabled')
      element.textContent = element.dataset.label
    })
  }

  showEditorLoading() {
    const container = this.editor.previousSibling
    if (container) {
      const overlay = document.createElement('div')
      overlay.className = 'editor-loading-overlay'
      overlay.innerHTML = `
        <div class="spinner-border text-primary"></div>
        <div class="mt-2 text-muted">Generiere Inhalt mit KI...</div>
      `
      container.style.position = 'relative'
      container.appendChild(overlay)
    }
  }

  clearEditorLoading() {
    const container = this.editor.previousSibling
    if (container) {
      const overlay = container.querySelector('.editor-loading-overlay')
      if (overlay) {
        overlay.remove()
      }
    }
  }

  disableTitleInput() {
    this.title.disabled = true
  }

  enableTitleInput() {
    this.title.disabled = false
  }
}
